import { useEffect, useMemo, useState } from 'react';
import { ApiError, api } from '@/lib/api';
import { Button, Combobox, Field, Modal, Textarea, type ComboboxOption } from '@/components/ui';
import { Notice } from '@/components/ui/Notice';

export interface TestVariantOption {
  id: number;
  label: string;
}

export interface TestSendDialogProps {
  open: boolean;
  onClose(): void;
  campaignId: number;
  defaultEmail: string;
  /** SAVED variants only — a test sends what is stored, not what is on screen. */
  variants: TestVariantOption[];
  /** True when the builder holds unsaved changes. */
  dirty: boolean;
}

const MAX_RECIPIENTS = 5;
const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function parseEmails(raw: string): string[] {
  return raw
    .split(/[\s,;]+/)
    .map((s) => s.trim())
    .filter(Boolean);
}

export function TestSendDialog({ open, onClose, campaignId, defaultEmail, variants, dirty }: TestSendDialogProps) {
  // Mounted fresh each time it opens, so the initial state is the reset.
  const [to, setTo] = useState(defaultEmail);
  const [variantId, setVariantId] = useState(variants[0] ? String(variants[0].id) : '');
  const [contactId, setContactId] = useState('');
  const [contactTerm, setContactTerm] = useState('');
  const [contactOptions, setContactOptions] = useState<ComboboxOption[]>([]);
  const [contactsLoading, setContactsLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [toError, setToError] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [sent, setSent] = useState<number | null>(null);

  useEffect(() => {
    if (!open) return undefined;
    const controller = new AbortController();
    const timer = window.setTimeout(() => {
      setContactsLoading(true);
      api
        .listContacts({ search: contactTerm || undefined, perPage: 20 }, controller.signal)
        .then((result) => {
          if (controller.signal.aborted) return;
          setContactOptions(
            result.data.map((contact) => ({
              value: String(contact.id),
              label: contact.name.trim() || `Contact #${contact.id}`,
              ...(contact.company ? { hint: contact.company.name } : {}),
            })),
          );
        })
        .catch(() => {
          if (controller.signal.aborted) return;
          setContactOptions([]);
        })
        .finally(() => {
          if (!controller.signal.aborted) setContactsLoading(false);
        });
    }, 250);
    return () => {
      window.clearTimeout(timer);
      controller.abort();
    };
  }, [open, contactTerm]);

  // Keep the chosen contact selectable after a new search drops it from the results.
  const [chosenContact, setChosenContact] = useState<ComboboxOption | null>(null);
  const contactChoices = useMemo<ComboboxOption[]>(
    () =>
      chosenContact && !contactOptions.some((o) => o.value === chosenContact.value)
        ? [chosenContact, ...contactOptions]
        : contactOptions,
    [chosenContact, contactOptions],
  );

  function chooseContact(value: string) {
    setContactId(value);
    setChosenContact(contactChoices.find((o) => o.value === value) ?? null);
  }

  const variantOptions = useMemo<ComboboxOption[]>(
    () => variants.map((v) => ({ value: String(v.id), label: v.label })),
    [variants],
  );

  async function submit() {
    setError(null);
    setToError(null);
    setSent(null);
    const emails = parseEmails(to);
    if (emails.length === 0) {
      setToError('Enter at least one address.');
      return;
    }
    if (emails.length > MAX_RECIPIENTS) {
      setToError(`At most ${MAX_RECIPIENTS} addresses.`);
      return;
    }
    const bad = emails.find((e) => !EMAIL.test(e));
    if (bad) {
      setToError(`${bad} is not an email address.`);
      return;
    }
    setSending(true);
    try {
      const result = await api.testMarketingCampaign(campaignId, {
        to: emails,
        ...(variantId ? { variantId: Number(variantId) } : {}),
        ...(contactId ? { contactId: Number(contactId) } : {}),
      });
      setSent(result.data.sent);
    } catch (caught) {
      if (caught instanceof ApiError) {
        const fields = caught.fieldErrors;
        const toField = Object.entries(fields).find(([k]) => k === 'to' || k.startsWith('to.'));
        if (toField) setToError(toField[1]);
        else setError([caught.formMessage ?? caught.message, ...caught.detailReasons].join(' '));
      } else {
        setError('Could not reach the server. Nothing was sent.');
      }
    } finally {
      setSending(false);
    }
  }

  return (
    <Modal
      open={open}
      onClose={() => {
        if (!sending) onClose();
      }}
      title="Send a test"
      size="sm"
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={sending}>
            Close
          </Button>
          <Button variant="primary" onClick={() => void submit()} loading={sending} disabled={variants.length === 0}>
            Send test
          </Button>
        </>
      }
    >
      <div className="grid gap-4">
        {dirty && (
          <Notice tone="warning">Unsaved changes are not in the test. It sends the saved draft.</Notice>
        )}
        {variants.length === 0 && (
          <p className="m-0 text-xs text-fg-muted">The saved draft has no email content to test.</p>
        )}
        <Field label={`Send to (up to ${MAX_RECIPIENTS})`} required error={toError}>
          {({ id, 'aria-describedby': describedBy }) => (
            <Textarea
              id={id}
              aria-describedby={describedBy}
              rows={2}
              value={to}
              disabled={sending}
              invalid={Boolean(toError)}
              onChange={(e) => setTo(e.target.value)}
            />
          )}
        </Field>
        {variants.length > 1 && (
          <Field label="Email">
            {({ id, 'aria-describedby': describedBy }) => (
              <Combobox
                id={id}
                aria-describedby={describedBy}
                value={variantId}
                onChange={setVariantId}
                options={variantOptions}
                searchPlaceholder="Search…"
                disabled={sending}
              />
            )}
          </Field>
        )}
        <Field label="Fill variables as">
          {({ id, 'aria-describedby': describedBy }) => (
            <Combobox
              id={id}
              aria-describedby={describedBy}
              value={contactId}
              onChange={chooseContact}
              options={contactChoices}
              emptyOptionLabel="Sample values"
              placeholder="Sample values"
              searchPlaceholder="Search contacts…"
              onSearch={setContactTerm}
              loading={contactsLoading}
              disabled={sending}
            />
          )}
        </Field>
        {error && (
          <Notice tone="danger" role="alert" className="[overflow-wrap:anywhere]">
            {error}
          </Notice>
        )}
        {sent !== null && (
          <Notice tone="success" role="status">
            Handed {sent} {sent === 1 ? 'test' : 'tests'} to the mail server. Check that {sent === 1 ? 'it arrives' : 'they arrive'}.
          </Notice>
        )}
      </div>
    </Modal>
  );
}
